/**
 * Closed-deal math and validation.
 *
 * Deliberately inverts the offer-terms model in shared/offerMath.ts. There the
 * MULTIPLE is canonical and EV re-derives from live EBITDA, which is right for
 * a live offer that a buyer quoted as a multiple. Here FINAL EV is canonical
 * and the multiple is derived, because a closing's hard fact is the dollar
 * amount in the purchase agreement.
 *
 * ebitdaAtClose is likewise a frozen snapshot, never a live read of
 * companies.ebitda. Re-reading it would let a later EBITDA restatement
 * silently rewrite what a historical deal closed at: restate Ellis from $9.4M
 * to $10.1M and a transaction that closed at 5.0x would redisplay as 4.65x,
 * with no error and no audit trail.
 */

export const CLOSING_OUTCOMES = ["Sold", "No Deal", "Withdrawn"] as const;
export type ClosingOutcome = (typeof CLOSING_OUTCOMES)[number];

export interface ClosingInput {
  outcome: ClosingOutcome;
  closedOn: string; // YYYY-MM-DD
  acquirerCompanyId: string | null;
  acquirerName: string | null;
  finalEv: number | null;
  ebitdaAtClose: number | null;
}

export interface ClosingRecord extends ClosingInput {
  companyId: string;
  notes: string | null;
  recordedBy: string | null;
  recordedAt: string;
  acquirerCompanyName?: string | null; // joined for display
}

export interface TrackRecord {
  soldCount: number;
  withTermsCount: number;
  totalEv: number | null;
  avgMultiple: number | null;
}

/** final EV / EBITDA at close. Null unless both are present and EBITDA > 0. */
export function deriveMultiple(
  finalEv: number | null | undefined,
  ebitdaAtClose: number | null | undefined
): number | null {
  if (finalEv == null || ebitdaAtClose == null) return null;
  if (!Number.isFinite(finalEv)) return null;
  if (!(ebitdaAtClose > 0)) return null; // also catches NaN
  return finalEv / ebitdaAtClose;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Mirrors the sold_has_terms CHECK constraint so the API rejects with a
 * readable message before Postgres rejects with a constraint name. Returns
 * every problem at once; a dialog that surfaces one error per save round-trip
 * is worse than one that surfaces all of them.
 */
export function validateClosing(input: ClosingInput): string[] {
  const errors: string[] = [];

  if (!(CLOSING_OUTCOMES as readonly string[]).includes(input.outcome)) {
    errors.push(`Outcome must be one of: ${CLOSING_OUTCOMES.join(", ")}`);
  }

  if (!input.closedOn || !input.closedOn.trim()) {
    errors.push("Close date is required");
  } else if (!ISO_DATE.test(input.closedOn.trim())) {
    errors.push("Close date must be YYYY-MM-DD");
  }

  if (input.outcome === "Sold") {
    if (!(Number(input.finalEv) > 0)) errors.push("Final enterprise value is required for a sale");
    if (!(Number(input.ebitdaAtClose) > 0)) errors.push("EBITDA at close is required for a sale");
    const hasAcquirer = !!input.acquirerCompanyId || !!input.acquirerName?.trim();
    if (!hasAcquirer) errors.push("Acquirer is required for a sale");
  }

  return errors;
}

/**
 * Track-record rollup. Counts sold deals, but computes money only over sold
 * deals whose terms were actually recorded, and reports both numbers so a
 * partially-filled set cannot present itself as complete.
 *
 * avgMultiple is a SIMPLE mean of each deal's multiple, not EV-weighted: it
 * answers "what multiple does a client typically get", not "what multiple did
 * our dollars transact at". Those are different questions and one large deal
 * should not dominate the first one.
 */
export function aggregateTrackRecord(
  rows: Array<{ outcome: ClosingOutcome; finalEv: number | null; ebitdaAtClose: number | null }>
): TrackRecord {
  const sold = rows.filter((r) => r.outcome === "Sold");
  const withTerms = sold
    .map((r) => ({ ev: r.finalEv, m: deriveMultiple(r.finalEv, r.ebitdaAtClose) }))
    .filter((r): r is { ev: number; m: number } => r.ev != null && r.m != null);

  return {
    soldCount: sold.length,
    withTermsCount: withTerms.length,
    totalEv: withTerms.length ? withTerms.reduce((s, r) => s + r.ev, 0) : null,
    avgMultiple: withTerms.length ? withTerms.reduce((s, r) => s + r.m, 0) / withTerms.length : null,
  };
}

/**
 * Display helper: "5.0x", or "n/a" when the multiple cannot be derived.
 * Plain text, no em dash: the firm bans them and this string can end up in
 * exported or copied output.
 */
export function formatClosingMultiple(m: number | null): string {
  return m == null ? "n/a" : `${m.toFixed(1)}x`;
}
